import type { ShopifyProduct, ShopifyProductVariant } from "@/types/shopify";

// ─────────────────────────────────────────────────────────
// Availability.
//
// Shopify's `availableForSale` means one thing: nothing here can be bought
// right now. It does NOT mean the product is new, unreleased, or on its way.
// The storefront previously rendered `!availableForSale` as "Coming Soon",
// which told customers a sold-out product was a future release.
//
// Purchasability is also not a dispatch promise. Nothing in this module says
// anything about how fast an order ships — see lib/content/business.ts.
// ─────────────────────────────────────────────────────────

export type Availability =
  /** At least one variant can be bought right now. */
  | "in-stock"
  /** The product exists and is published, but every variant is out of stock. */
  | "sold-out"
  /** This particular option combination is not stocked; others may be. */
  | "unavailable-combination"
  /** In the catalog, but not listed for online sale (no Shopify listing). */
  | "not-sold-online";

export const AVAILABILITY_LABEL: Record<Availability, string> = {
  "in-stock": "In stock",
  "sold-out": "Sold out",
  "unavailable-combination": "Not available in this combination",
  "not-sold-online": "Not sold online",
};

/**
 * Longer explanation, used where the customer needs to know what to do next.
 * Never promises a restock date — we have none.
 */
export const AVAILABILITY_DETAIL: Record<Availability, string | null> = {
  "in-stock": null,
  "sold-out": "Every option for this set is currently out of stock.",
  "unavailable-combination":
    "We don't stock this combination. Pick another option to continue.",
  "not-sold-online":
    "This set is in our catalog but isn't listed for online ordering. Build the same specification in the configurator, or get in touch.",
};

/** Availability of a whole product, ignoring which variant is selected. */
export function productAvailability(product: ShopifyProduct): Availability {
  if (product.variants.nodes.some((v) => v.availableForSale)) return "in-stock";
  return "sold-out";
}

/**
 * Availability of the exact thing the customer is about to buy.
 *
 * `variant === null` means the chosen option combination does not resolve to a
 * variant at all — a different situation from a variant that exists and is out
 * of stock, and the two must not be shown the same way.
 */
export function variantAvailability(
  product: ShopifyProduct,
  variant: ShopifyProductVariant | null | undefined,
): Availability {
  if (!variant) {
    // Distinguish "this combination doesn't exist" from "nothing here is
    // buyable at all".
    return productAvailability(product) === "sold-out"
      ? "sold-out"
      : "unavailable-combination";
  }
  return variant.availableForSale ? "in-stock" : "unavailable-combination";
}

/** True only when this exact variant can be added to the cart. */
export function canPurchase(variant: ShopifyProductVariant | null | undefined): boolean {
  return !!variant?.availableForSale;
}
